<svelte:options tag={null} />

<script lang="ts">
  import { tick } from 'svelte';
  import { get_current_component } from 'svelte/internal';
  import { EventHandler } from '../../utilities/EventHandler';
  import { clickOutside } from '../../utilities/ClickOutside';
  import type { ListboxItems } from '../listbox/listbox.types';
  import type { TextInputSize } from '../textinput/textinput.types';

  import Tag from '../tag/Tag.nested.svelte';
  import Loader from '../loader/Loader.nested.svelte';
  import TextInput from '../textinput/Textinput.nested.svelte';
  import Listbox from '../listbox/Listbox.nested.svelte';

  export let maxwidth = '100%';
  export let tagid: string;
  export let taglabel = '';
  export let placeholder: string;
  export let filter = true;
  export let disabled = false;
  export let size: TextInputSize = 'm';
  export let isinvalid: boolean;
  export let inputvalue: string;
  export let id: string;
  export let loading = false;
  export let required = false;
  export let items: string;
  export let value: any;
  export let open = false;
  export let multiple = false;
  export let emptysearchlabel = 'No results found';
  export let datatextexpr = 'label';
  export let datavalueexpr = 'value';
  export let nosort = false;
  export let layertitle = 'Title layer';
  export let showmoretagslabel = 'Show more';
  export let maxnumbertoshowtags = 12;
  export let alltags = false;

  const component: any = get_current_component();
  const uuid = Math.random();
  let eventHandler = new EventHandler();
  let tag: any;
  let tagWidth = '0px';
  let tagValue: any;
  let sortedListItems: any;
  let isfiltered = false;
  let selectedItems: ListboxItems[] = [];
  let openLayer: any = null;

  $: localInputValue = inputvalue;
  $: parsedItems = items ? (JSON.parse(items) as ListboxItems[]) : [];
  $: localItems = parsedItems;
  $: listboxValue = !value && multiple ? [] : value ? JSON.parse(value) : value;
  $: setTagLabel = () => {
    return listboxValue.length.toString() + ' ' + taglabel;
  };
  $: watchListboxValue(listboxValue);
  $: setTagWidth(tagValue);
  $: openState = open;
  $: {
    const eventName = openState ? 'open' : 'close';
    eventHandler.dispatch(eventName, openState);
  }
  $: attributes = { ...$$restProps };
  $: delete attributes.class;

  function clearListbox() {
    listboxValue = multiple ? [] : '';
    closeListBox();
    eventHandler.dispatch('clear', true);
  }

  function closeListBox() {
    openState = false;

    if (multiple && !nosort) {
      sortItems();
    } else {
      localItems = parsedItems;
    }
  }

  function onChange() {
    eventHandler.dispatch('onchange', listboxValue);

    if (!multiple) {
      closeListBox();
    }
  }

  function getSelectedItems(val?: string) {
    const value = val ? val : listboxValue;

    const selectedItems = parsedItems.filter((item: ListboxItems) => {
      return multiple
        ? value.includes(item[datavalueexpr])
        : value === item[datavalueexpr];
    });
    return selectedItems;
  }

  function sortItems() {
    sortedListItems = parsedItems;
    const selectedItems = getSelectedItems();

    sortedListItems.sort((a: any, b: any) => {
      const hasItemA = selectedItems.includes(a);
      const hasItemB = selectedItems.includes(b);

      if (hasItemA === hasItemB) {
        return a[datavalueexpr] - b[datavalueexpr];
      } else if (hasItemA < hasItemB) {
        return 1;
      } else {
        return -1;
      }
    });

    localItems = sortedListItems;
  }

  function openLayerFn() {
    openLayer = true;
  }

  function closeLayerFn() {
    openLayer = null;
  }

  function handleInputValue(value: any) {
    eventHandler.dispatch('oninput', value);
  }

  function filterListbox(value: any) {
    if (!filter) {
      return;
    }

    if (value.length) {
      localItems = parsedItems.filter((item: any) =>
        item[datatextexpr].toUpperCase().includes(value.toUpperCase()),
      );
    } else {
      localItems = parsedItems;
    }

    isfiltered = !localItems.length;

    eventHandler.dispatch('onfilter', localItems);
  }

  function onInput(e: Event) {
    const value = (e.target as HTMLInputElement).value;
    handleInputValue(value);
    filterListbox(value);
  }

  function onScrollEnd() {
    eventHandler.dispatch('scrollend', true);
  }

  function removeTagSelected(itemSelectedId: any) {
    listboxValue = listboxValue.filter(
      (value: any) => value !== itemSelectedId,
    );
    eventHandler.dispatch('onchange', listboxValue);
  }

  // Watch Functions
  async function watchListboxValue(value: any) {
    await tick();
    selectedItems = getSelectedItems(value);
    if (!multiple) {
      const seletedLabels = selectedItems.map((item) => item[datatextexpr]);
      localInputValue = seletedLabels.join(', ');
    } else {
      tagValue = value;
    }
  }

  async function setTagWidth(tagValue: any) {
    await tick();

    tagWidth =
      tag && tagValue && tagValue.length
        ? `${
            component.shadowRoot.querySelector('.mc-tag-removable').clientWidth
          }px`
        : '0px';
  }
</script>

<div
  class="mc-autocomplete"
  class:mc-autocomplete--multi={multiple}
  style="--autocomplete-tag-width: {tagWidth}; --autocomplete-width: {maxwidth};"
  use:clickOutside
  on:outclick={closeListBox}
  {...attributes}
>
  <!--  TAG REMOVABLE -->
  {#if multiple && listboxValue.length > 0 && !alltags}
    <Tag
      id={tagid ? tagid : `autocompleteTag-${uuid}`}
      class="mc-autocomplete__tag"
      label={setTagLabel()}
      size="s"
      type="removable"
      dark={false}
      disabled={false}
      href=""
      name=""
      selected={false}
      bind:this={tag}
      on:tag-removed={clearListbox}
    />
  {/if}

  <!-- LOADER -->
  {#if loading}
    <Loader size="s" theme="primary" text="" class="mc-autocomplete__loader" />
  {/if}

  <!-- TEXT INPUT -->
  <TextInput
    classinput="mc-autocomplete__trigger"
    name={id}
    bind:value={localInputValue}
    {placeholder}
    inputtype="text"
    {isinvalid}
    {disabled}
    {size}
    {required}
    iconname="navigation-display-search-24px"
    rightalign={false}
    isvalid={false}
    min=""
    max=""
    on:input={onInput}
    on:click={() => (openState = true)}
  />
  {#if localInputValue != null && localInputValue != ''}
    <button
      class="mc-autocomplete__clear"
      type="button"
      on:click|stopPropagation={() => {
        localInputValue = '';
        localItems = parsedItems;
        isfiltered = false;
        if (!multiple) {
          clearListbox();
        }
      }}
    >
      <navigation-control-tag--cross-24px />
      <span class="mc-autocomplete__clear-text">Clear</span>
    </button>
  {/if}

  <!-- LISTBOX -->
  <Listbox
    items={JSON.stringify(localItems)}
    open={openState}
    {multiple}
    {isfiltered}
    {emptysearchlabel}
    {maxwidth}
    {datatextexpr}
    {datavalueexpr}
    bind:value={listboxValue}
    on:change={onChange}
    on:scrollend={onScrollEnd}
  />

  {#if multiple && alltags}
    <div class="tags-selected">
      {#each selectedItems as selectedItem, index}
        {#if index < maxnumbertoshowtags}
          <div class="tag-selected">
            <m-tag
              id={selectedItem.id}
              label={selectedItem.label}
              size="s"
              type="removable"
              on:tag-removed={() => removeTagSelected(selectedItem.id)}
            />
          </div>
        {/if}
      {/each}
      {#if listboxValue.length > maxnumbertoshowtags}
        <div class="tag-selected">
          <m-tag
            id="id-tag"
            label="({listboxValue.length}) {showmoretagslabel} "
            size="s"
            type="link"
            on:tag-selected={() => openLayerFn()}
          />
        </div>
      {/if}
    </div>
  {/if}

  <span class="mc-autocomplete__tag" style="display:none;" />
  <span class="mc-autocomplete__loader" style="display:none;" />
  <span class="mc-text-input mc-autocomplete__trigger" style="display:none;" />

  <div>
    <m-layer
      title={layertitle}
      layertitletag="h3"
      isopen={openLayer}
      on:layer-closed={() => closeLayerFn()}
    >
      <small slot="content">
        <div class="tags-selected">
          {#each selectedItems as selectedItem}
            <div class="tag-selected">
              <m-tag
                id={selectedItem.id}
                label={selectedItem.label}
                size="s"
                type="removable"
                on:tag-removed={() => removeTagSelected(selectedItem.id)}
              />
            </div>
          {/each}
        </div>
      </small>
    </m-layer>
  </div>
</div>

<style lang="scss">
  @import '@mozaic-ds/styles/settings-tools/all-settings';
  @import '@mozaic-ds/styles/components/c.autocomplete';

  // Autocomplete
  // NOTE: These styles with the "!important" allow to overload the edge effects
  // due to the scoping applied by Svelte on the CSS selectors
  .mc-autocomplete {
    &__tag {
      position: absolute !important; // due to the Svelte CSS scope
    }

    &--multi {
      .mc-autocomplete__trigger {
        padding-left: calc(
          2.9375rem + var(--autocomplete-tag-width)
        ) !important;
      }
    }
  }

  // Selected Tags
  .tags-selected {
    display: flex;
    flex-wrap: wrap;
  }

  .tag-selected {
    margin-top: 1em;
    margin-left: 5px;
    margin-right: 5px;
  }
</style>
